/**
 * ReferralList
 *
 * Groups referrals by referrer. Each referrer row is collapsible,
 * showing a summary (count, total reward, Pay All) with an expandable
 * sub-table of individual referrals and their action buttons.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getRequest, postRequest } from '../../utils/httpRequest';
import { API_ENDPOINTS, ITEMS_PER_PAGE, REFERRAL_STATUSES } from './constants';
import { getStatusBadgeStyle, getActionButtons, formatReferralDate } from './utils';
import { colors, commonStyles, gradients, modalStyles } from '../AgenticPaymentTransactionHistory/styles';

// ── Shared style constants ────────────────────────────────────────────────────

const selectStyle = {
  padding: '8px 12px',
  paddingRight: '36px',
  border: `1px solid ${colors.gray400}`,
  borderRadius: '4px',
  fontSize: '13px',
  background: colors.white,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  backgroundSize: '12px',
  cursor: 'pointer',
  minWidth: '140px',
  appearance: 'none',
  WebkitAppearance: 'none',
};

const ACTION_STYLES = {
  pay:     { background: 'linear-gradient(135deg, #28a745, #218838)', color: '#fff' },
  hold:    { background: 'linear-gradient(135deg, #ffc107, #e0a800)', color: '#333' },
  reject:  { background: 'linear-gradient(135deg, #dc3545, #c82333)', color: '#fff' },
  release: { background: 'linear-gradient(135deg, #17a2b8, #138496)', color: '#fff' },
};

const ACTION_LABELS = { pay: 'Pay', hold: 'Hold', reject: 'Reject', release: 'Release' };

const actionBtnBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '5px 12px',
  border: 'none',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  marginRight: '4px',
  letterSpacing: '0.2px',
  lineHeight: '1.4',
  WebkitAppearance: 'none',
  appearance: 'none',
  textShadow: 'none',
  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
};

const subThStyle = {
  padding: '9px 14px',
  textAlign: 'left',
  color: colors.primary,
  fontWeight: 700,
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  whiteSpace: 'nowrap',
  background: '#eef1f8',
  borderBottom: `2px solid ${colors.border}`,
};

const subTdStyle = {
  padding: '10px 14px',
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle',
  fontSize: '13px',
};

// ── ReasonModal ───────────────────────────────────────────────────────────────

const ReasonModal = ({ action, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  const isHold     = action === 'hold';
  const accentColor = isHold ? '#e0a800' : '#c82333';
  const accentGrad  = isHold
    ? 'linear-gradient(135deg, #ffc107, #e0a800)'
    : 'linear-gradient(135deg, #dc3545, #c82333)';

  return (
    <div style={modalStyles.overlay} onClick={onCancel}>
      <div style={{ ...modalStyles.content, maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...modalStyles.header, borderBottom: `3px solid ${accentColor}` }}>
          <h3 style={modalStyles.headerTitle}>{isHold ? '⏸ Hold Referral' : '✕ Reject Referral'}</h3>
          <button type="button" onClick={onCancel} style={modalStyles.closeButton}>✕ Cancel</button>
        </div>
        <div style={{ padding: '24px' }}>
          <label htmlFor="reason-input" style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: colors.primary, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Reason <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <textarea
            id="reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={`Enter reason for ${action}...`}
            rows={4}
            autoFocus
            style={{ width: '100%', padding: '10px 14px', border: `1px solid ${colors.gray400}`, borderRadius: '4px', fontSize: '14px', color: colors.gray700, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button type="button" onClick={onCancel} style={{ ...actionBtnBase, background: colors.gray200, color: colors.gray700, padding: '8px 20px', fontSize: '13px' }}>Cancel</button>
            <button
              type="button"
              disabled={!reason.trim()}
              onClick={() => reason.trim() && onConfirm(reason.trim())}
              style={{ ...actionBtnBase, background: reason.trim() ? accentGrad : colors.gray300, color: reason.trim() ? (isHold ? '#333' : '#fff') : colors.gray500, cursor: reason.trim() ? 'pointer' : 'not-allowed', padding: '8px 20px', fontSize: '13px' }}
            >
              Confirm {ACTION_LABELS[action]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── ReferrerGroup ─────────────────────────────────────────────────────────────
// One collapsible row per referrer. Header shows summary + Pay All.
// Expanded body shows a sub-table of individual referrals.

const ReferrerGroup = ({ group, adminUserId, onViewDetail, actionLoading, onAction, onPayAll, payAllLoading, forceExpanded }) => {
  const [localExpanded, setLocalExpanded] = useState(false);
  const expanded = forceExpanded || localExpanded;

  // Aggregate stats for the header
  const awaitingCount = group.referrals.filter((r) => r.status === REFERRAL_STATUSES.AWAITING_PAYMENT).length;
  const totalRaw      = group.referrals.reduce((sum, r) => sum + (r.reward_amount_raw || 0), 0);
  const currency      = group.referrals[0]?.reward_currency || '';

  // Format total, use the same currency symbol as the first referral's reward_amount string
  const currencySymbol = group.referrals[0]?.reward_amount?.replace(/[\d.,\s]/g, '') || '';
  const totalFormatted = currencySymbol
    ? `${currencySymbol}${totalRaw.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `${totalRaw.toFixed(2)} ${currency}`;

  return (
    <>
      {/* ── Referrer header row ── */}
      <tr
        onClick={() => setLocalExpanded((v) => !v)}
        style={{
          background: expanded
            ? 'linear-gradient(135deg, #2e3b61 0%, #3d4d7a 100%)'
            : 'linear-gradient(135deg, #f0f3fa 0%, #e8ecf5 100%)',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background 0.15s ease',
        }}
      >
        {/* Chevron + email */}
        <td colSpan={2} style={{ padding: '13px 16px', fontWeight: 700, fontSize: '13px', color: expanded ? '#fff' : colors.primary, borderBottom: `1px solid ${colors.border}`, whiteSpace: 'nowrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '22px', height: '22px', borderRadius: '50%',
              background: expanded ? 'rgba(255,255,255,0.2)' : colors.primary,
              color: '#fff', fontSize: '11px', fontWeight: 700,
              transition: 'transform 0.2s ease',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              flexShrink: 0,
            }}>▶</span>
            {group.referrer_email}
          </span>
        </td>

        {/* Referral count badge */}
        <td style={{ padding: '13px 16px', borderBottom: `1px solid ${colors.border}` }}>
          <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 700,
            background: expanded ? 'rgba(255,255,255,0.2)' : colors.primary,
            color: '#fff',
          }}>
            {group.referrals.length} referral{group.referrals.length !== 1 ? 's' : ''}
          </span>
        </td>

        {/* Awaiting count */}
        <td style={{ padding: '13px 16px', borderBottom: `1px solid ${colors.border}`, fontSize: '12px', color: expanded ? 'rgba(255,255,255,0.8)' : colors.gray600 }}>
          {awaitingCount > 0
            ? <span style={{ background: expanded ? 'rgba(255,193,7,0.25)' : '#fff3cd', color: expanded ? '#ffc107' : '#856404', padding: '3px 8px', borderRadius: '10px', fontWeight: 600, fontSize: '11px' }}>{awaitingCount} awaiting</span>
            : <span style={{ color: expanded ? 'rgba(255,255,255,0.4)' : colors.gray400, fontSize: '12px' }}>-</span>
          }
        </td>

        {/* Total reward */}
        <td colSpan={3} style={{ padding: '13px 16px', borderBottom: `1px solid ${colors.border}`, fontWeight: 700, fontSize: '13px', color: expanded ? '#fff' : colors.gray700 }}>
          {totalFormatted}
        </td>

        {/* Pay All button, only when 2+ awaiting */}
        <td colSpan={2} style={{ padding: '13px 16px', borderBottom: `1px solid ${colors.border}`, textAlign: 'right' }}>
          {awaitingCount >= 2 && (
            <button
              type="button"
              disabled={!!payAllLoading}
              onClick={(e) => { e.stopPropagation(); onPayAll(group.referrer_user_id); }}
              style={{
                ...actionBtnBase,
                background: payAllLoading ? colors.gray300 : 'linear-gradient(135deg, #28a745, #218838)',
                color: payAllLoading ? colors.gray500 : '#fff',
                opacity: payAllLoading ? 0.6 : 1,
                cursor: payAllLoading ? 'not-allowed' : 'pointer',
                padding: '6px 14px',
                fontSize: '12px',
                marginRight: 0,
              }}
            >
              {payAllLoading ? '...' : `Pay All (${awaitingCount})`}
            </button>
          )}
        </td>
      </tr>

      {/* ── Expanded sub-table ── */}
      {expanded && (
        <tr>
          <td colSpan={9} style={{ padding: 0, background: '#f7f9ff', borderBottom: `2px solid ${colors.primary}` }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>
                    {['Referral ID', 'Referee Name', 'Country', 'Currency', 'Reward', 'Payout Pref', 'Status', 'Created At', 'Actions'].map((h) => (
                      <th key={h} style={subThStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.referrals.map((referral, idx) => {
                    const badgeStyle = getStatusBadgeStyle(referral.status);
                    const actions    = getActionButtons(referral.status);
                    const rowLoading = actionLoading[referral.id];

                    return (
                      <tr key={referral.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f4f6fc' }}>
                        <td style={subTdStyle}>
                          <code style={{ fontSize: '11px', fontFamily: 'monospace', background: colors.gray200, padding: '2px 6px', borderRadius: '3px' }}>
                            {referral.referral_ext_id || referral.id}
                          </code>
                        </td>
                        <td style={subTdStyle}>{referral.referee_name || '-'}</td>
                        <td style={subTdStyle}>{referral.country || '-'}</td>
                        <td style={subTdStyle}>{referral.reward_currency || '-'}</td>
                        <td style={subTdStyle}><strong>{referral.reward_amount || '-'}</strong></td>
                        <td style={subTdStyle}>
                          {referral.payout_preference ? (
                            <span style={{
                              display: 'inline-block', padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                              background: referral.payout_preference === 'local' ? '#d4edda' : '#cce5ff',
                              color: referral.payout_preference === 'local' ? '#155724' : '#004085',
                              textTransform: 'uppercase',
                            }}>
                              {referral.payout_preference}
                            </span>
                          ) : '-'}
                        </td>
                        <td style={subTdStyle}>
                          <span style={{ ...badgeStyle, display: 'inline-block', padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' }}>
                            {referral.status}
                          </span>
                        </td>
                        <td style={{ ...subTdStyle, whiteSpace: 'nowrap' }}>{formatReferralDate(referral.created_at)}</td>
                        <td style={{ ...subTdStyle, whiteSpace: 'nowrap' }}>
                          <button type="button" onClick={() => onViewDetail(referral)} style={{ ...actionBtnBase, background: gradients.primary, color: '#fff', fontSize: '11px', padding: '4px 10px' }}>
                            View
                          </button>
                          {actions.map((action) => (
                            <button
                              key={action}
                              type="button"
                              disabled={!!rowLoading}
                              onClick={() => onAction(referral.id, action)}
                              style={{ ...actionBtnBase, ...ACTION_STYLES[action], fontSize: '11px', padding: '4px 10px', opacity: rowLoading ? 0.45 : 1, cursor: rowLoading ? 'not-allowed' : 'pointer' }}
                            >
                              {rowLoading === action ? '...' : ACTION_LABELS[action]}
                            </button>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ── ReferralList ──────────────────────────────────────────────────────────────

const ReferralList = ({ adminUserId, onViewDetail }) => {
  const [referrals, setReferrals]           = useState([]);
  const [totalCount, setTotalCount]         = useState(0);
  const [totalPages, setTotalPages]         = useState(1);
  const [currentPage, setCurrentPage]       = useState(1);
  const [loadingMore, setLoadingMore]       = useState(false);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [statusFilter, setStatusFilter]     = useState('');
  const [countryFilter, setCountryFilter]   = useState('');
  const [countryInput, setCountryInput]     = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [currencyInput, setCurrencyInput]   = useState('');
  const [monthFilter, setMonthFilter]       = useState('');
  const [yearFilter, setYearFilter]         = useState('');
  const [searchQuery, setSearchQuery]       = useState('');
  const [searchInput, setSearchInput]       = useState('');
  const [sortBy, setSortBy]                 = useState('');
  const [sortOrder, setSortOrder]           = useState('DESC');
  const [actionLoading, setActionLoading]   = useState({});
  const [payAllLoading, setPayAllLoading]   = useState({});
  const [actionError, setActionError]       = useState(null);
  const [reasonModal, setReasonModal]       = useState(null);
  const [allExpanded, setAllExpanded]       = useState(false);

  // Track current filters in a ref so action refresh can re-fetch all loaded pages
  const filtersRef = React.useRef({ statusFilter: '', countryFilter: '', currencyFilter: '', monthFilter: '', yearFilter: '', searchQuery: '', sortBy: '', sortOrder: 'DESC' });

  // Group flat referrals by referrer_user_id, preserving server sort order
  const groups = useMemo(() => {
    const map = new Map();
    referrals.forEach((r) => {
      const key = r.referrer_user_id || r.referrer_email || 'unknown';
      if (!map.has(key)) {
        map.set(key, { referrer_user_id: key, referrer_email: r.referrer_email, referrals: [] });
      }
      map.get(key).referrals.push(r);
    });
    return Array.from(map.values());
  }, [referrals]);

  // Fetch a single page, append=true accumulates, append=false resets
  const fetchPage = useCallback(async (page, filters, append = false) => {
    const { status, country, currency, month, year, search, sby, sorder } = filters;
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: ITEMS_PER_PAGE.toString() });
      if (status)   params.set('status', status);
      if (country)  params.set('country', country);
      if (currency) params.set('currency', currency);
      if (month)    params.set('month', month);
      if (year)     params.set('year', year);
      if (search)   params.set('search', search);
      if (sby)      params.set('sortBy', sby);
      if (sorder)   params.set('sortOrder', sorder);

      const data = await getRequest(`${API_ENDPOINTS.LIST}?${params.toString()}`);
      if (!data.success) throw new Error(data.error || 'Failed to load referrals');

      const incoming = data.data?.referrals || [];
      setReferrals((prev) => append ? [...prev, ...incoming] : incoming);
      setTotalCount(data.data?.total_count || 0);
      setTotalPages(data.data?.total_pages || 1);
      setCurrentPage(page);
    } catch (err) {
      setError(err.message || 'Failed to load referrals');
      if (!append) setReferrals([]);
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, []);

  const currentFilters = useCallback(() => ({
    status: statusFilter, country: countryFilter, currency: currencyFilter,
    month: monthFilter, year: yearFilter, search: searchQuery, sby: sortBy, sorder: sortOrder,
  }), [statusFilter, countryFilter, currencyFilter, monthFilter, yearFilter, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    fetchPage(1, { status: '', country: '', currency: '', month: '', year: '', search: '', sby: '', sorder: 'DESC' }, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply new filters, always reset to page 1
  const applyFilters = useCallback((status, country, currency, month, year, search, sby, sorder) => {
    const filters = { status, country, currency, month, year, search, sby, sorder };
    filtersRef.current = { statusFilter: status, countryFilter: country, currencyFilter: currency, monthFilter: month, yearFilter: year, searchQuery: search, sortBy: sby, sortOrder: sorder };
    fetchPage(1, filters, false);
  }, [fetchPage]);

  // Load next page, accumulate
  const handleLoadMore = useCallback(() => {
    const nextPage = currentPage + 1;
    fetchPage(nextPage, currentFilters(), true);
  }, [currentPage, currentFilters, fetchPage]);

  // After an action, re-fetch all currently loaded pages and merge
  const refreshAllLoaded = useCallback(async () => {
    const filters = currentFilters();
    const pagesToLoad = currentPage;
    setLoading(true);
    try {
      const pages = await Promise.all(
        Array.from({ length: pagesToLoad }, (_, i) =>
          getRequest(`${API_ENDPOINTS.LIST}?${new URLSearchParams({
            page: (i + 1).toString(), limit: ITEMS_PER_PAGE.toString(),
            ...(filters.status   && { status:   filters.status }),
            ...(filters.country  && { country:  filters.country }),
            ...(filters.currency && { currency: filters.currency }),
            ...(filters.month    && { month:    filters.month }),
            ...(filters.year     && { year:     filters.year }),
            ...(filters.search   && { search:   filters.search }),
            ...(filters.sby      && { sortBy:   filters.sby }),
            ...(filters.sorder   && { sortOrder: filters.sorder }),
          }).toString()}`)
        )
      );
      const merged = pages.flatMap((d) => d.success ? (d.data?.referrals || []) : []);
      // Deduplicate by id in case of overlap
      const seen = new Set();
      setReferrals(merged.filter((r) => { if (seen.has(r.id)) return false; seen.add(r.id); return true; }));
      const last = pages[pages.length - 1];
      if (last?.success) {
        setTotalCount(last.data?.total_count || 0);
        setTotalPages(last.data?.total_pages || 1);
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentFilters]);

  const handleStatusChange    = (val) => { setStatusFilter(val);  applyFilters(val, countryFilter, currencyFilter, monthFilter, yearFilter, searchQuery, sortBy, sortOrder); };
  const handleMonthChange     = (val) => { setMonthFilter(val);   applyFilters(statusFilter, countryFilter, currencyFilter, val, yearFilter, searchQuery, sortBy, sortOrder); };
  const handleYearChange      = (val) => { setYearFilter(val);    applyFilters(statusFilter, countryFilter, currencyFilter, monthFilter, val, searchQuery, sortBy, sortOrder); };
  const handleSortByChange    = (val) => { setSortBy(val);        applyFilters(statusFilter, countryFilter, currencyFilter, monthFilter, yearFilter, searchQuery, val, sortOrder); };
  const handleSortOrderChange = (val) => { setSortOrder(val);     applyFilters(statusFilter, countryFilter, currencyFilter, monthFilter, yearFilter, searchQuery, sortBy, val); };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInput);
    applyFilters(statusFilter, countryFilter, currencyFilter, monthFilter, yearFilter, searchInput, sortBy, sortOrder);
  };
  const handleCountrySubmit = () => {
    setCountryFilter(countryInput);
    applyFilters(statusFilter, countryInput, currencyFilter, monthFilter, yearFilter, searchQuery, sortBy, sortOrder);
  };
  const handleCurrencySubmit = () => {
    setCurrencyFilter(currencyInput);
    applyFilters(statusFilter, countryFilter, currencyInput, monthFilter, yearFilter, searchQuery, sortBy, sortOrder);
  };

  const handleAction = useCallback((referralId, action) => {
    if (action === 'hold' || action === 'reject') {
      setReasonModal({ referralId, action });
    } else {
      executeAction(referralId, action, null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const executeAction = useCallback(async (referralId, action, reason) => {
    setReasonModal(null);
    setActionLoading((prev) => ({ ...prev, [referralId]: action }));
    setActionError(null);
    try {
      const url     = API_ENDPOINTS[action.toUpperCase()](referralId);
      const payload = { admin_user_id: adminUserId };
      if (reason) payload.reason = reason;
      const data = await postRequest(url, payload);
      if (!data.success) throw new Error(data.error || `Failed to ${action} referral`);
      await refreshAllLoaded();
    } catch (err) {
      setActionError(err.message || `Failed to ${action} referral`);
    } finally {
      setActionLoading((prev) => { const next = { ...prev }; delete next[referralId]; return next; });
    }
  }, [adminUserId, refreshAllLoaded]);

  const handlePayAll = useCallback(async (referrerUserId) => {
    setPayAllLoading((prev) => ({ ...prev, [referrerUserId]: true }));
    setActionError(null);
    try {
      const data = await postRequest(API_ENDPOINTS.PAY_ALL(referrerUserId), { admin_user_id: adminUserId });
      if (!data.success) throw new Error(data.error || 'Failed to pay all referrals');
      await refreshAllLoaded();
    } catch (err) {
      setActionError(err.message || 'Failed to pay all referrals');
    } finally {
      setPayAllLoading((prev) => { const next = { ...prev }; delete next[referrerUserId]; return next; });
    }
  }, [adminUserId, refreshAllLoaded]);

  const loadedCount = referrals.length;
  const hasMore     = currentPage < totalPages;

  return (
    <div>
      {reasonModal && (
        <ReasonModal
          action={reasonModal.action}
          onConfirm={(reason) => executeAction(reasonModal.referralId, reasonModal.action, reason)}
          onCancel={() => setReasonModal(null)}
        />
      )}

      {/* Filters */}
      <div style={{ ...commonStyles.filterBar, flexDirection: 'column', alignItems: 'stretch', gap: '10px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
            placeholder="Search by referee name, referral ID, or email..."
            style={{ ...commonStyles.input, flex: '1', minWidth: '220px' }}
            data-testid="referral-search"
          />
          <button
            type="button"
            onClick={handleSearchSubmit}
            style={{ padding: '0 18px', height: '40px', background: gradients.primary, color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Search
          </button>
          <select value={statusFilter} onChange={(e) => handleStatusChange(e.target.value)} style={selectStyle} data-testid="referral-status-filter">
            <option value="">All Statuses</option>
            <option value={REFERRAL_STATUSES.NOT_ELIGIBLE}>Not Eligible</option>
            <option value={REFERRAL_STATUSES.AWAITING_PAYMENT}>Awaiting Payment</option>
            <option value={REFERRAL_STATUSES.PAID}>Paid</option>
            <option value={REFERRAL_STATUSES.HELD}>Held</option>
            <option value={REFERRAL_STATUSES.REJECTED}>Rejected</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            value={countryInput}
            onChange={(e) => {
              const val = e.target.value.toUpperCase();
              setCountryInput(val);
              if (val === '') { setCountryFilter(''); applyFilters(statusFilter, '', currencyFilter, monthFilter, yearFilter, searchQuery, sortBy, sortOrder); }
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleCountrySubmit()}
            placeholder="Country (e.g. NG)"
            style={{ ...commonStyles.input, width: '160px' }}
            data-testid="referral-country-filter"
          />
          <input
            type="text"
            value={currencyInput}
            onChange={(e) => {
              const val = e.target.value.toUpperCase();
              setCurrencyInput(val);
              if (val === '') { setCurrencyFilter(''); applyFilters(statusFilter, countryFilter, '', monthFilter, yearFilter, searchQuery, sortBy, sortOrder); }
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleCurrencySubmit()}
            placeholder="Currency (e.g. NGN)"
            style={{ ...commonStyles.input, width: '175px' }}
            data-testid="referral-currency-filter"
          />
          <select value={monthFilter} onChange={(e) => handleMonthChange(e.target.value)} style={{ ...selectStyle, minWidth: '110px' }} data-testid="referral-month-filter">
            <option value="">All Months</option>
            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <select value={yearFilter} onChange={(e) => handleYearChange(e.target.value)} style={{ ...selectStyle, minWidth: '100px' }} data-testid="referral-year-filter">
            <option value="">All Years</option>
            {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => 2024 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select value={sortBy} onChange={(e) => handleSortByChange(e.target.value)} style={{ ...selectStyle, minWidth: '130px' }}>
            <option value="">Sort By</option>
            <option value="created_at">Created At</option>
            <option value="reward_amount">Reward Amount</option>
            <option value="status">Status</option>
          </select>
          <select value={sortOrder} onChange={(e) => handleSortOrderChange(e.target.value)} style={{ ...selectStyle, minWidth: '90px' }}>
            <option value="DESC">Newest</option>
            <option value="ASC">Oldest</option>
          </select>
        </div>
      </div>

      {/* Action error */}
      {actionError && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: '4px', marginBottom: '12px', fontSize: '14px' }}>
          <span>{actionError}</span>
          <button type="button" onClick={() => setActionError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, color: '#721c24', fontSize: '16px' }}>✕</button>
        </div>
      )}

      {/* Expand / Collapse All toggle */}
      {!loading && !error && groups.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <button
            type="button"
            onClick={() => setAllExpanded((v) => !v)}
            style={{ ...actionBtnBase, background: colors.gray200, color: colors.primary, padding: '6px 14px', fontSize: '12px', boxShadow: 'none' }}
          >
            {allExpanded ? '⊖ Collapse All' : '⊕ Expand All'}
          </button>
        </div>
      )}

      {/* Table */}
      <div style={{ ...commonStyles.panel, marginBottom: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table data-testid="referral-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['Referrer', 'Email', 'Referrals', 'Awaiting', 'Total Reward', '', '', 'Actions', ''].map((h, i) => (
                  <th key={i} style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                    background: 'linear-gradient(135deg, #2e3b61 0%, #3d4d7a 100%)',
                    borderBottom: '2px solid #e58734',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ width: '32px', height: '32px', border: `3px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                    <p style={{ color: colors.gray600, fontSize: '14px', margin: 0 }}>Loading referrals...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: '#721c24', marginBottom: '12px' }}>{error}</p>
                    <button type="button" onClick={() => fetchReferrals(currentPage, statusFilter, countryFilter, currencyFilter, monthFilter, yearFilter, searchQuery, sortBy, sortOrder)} style={{ padding: '8px 16px', background: colors.primary, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
                  </td>
                </tr>
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '60px 20px', textAlign: 'center', background: colors.gray100 }}>
                    <p style={{ margin: 0, color: colors.gray600, fontSize: '14px' }}>No referrals found.</p>
                  </td>
                </tr>
              ) : (
                groups.map((group) => (
                  <ReferrerGroup
                    key={group.referrer_user_id}
                    group={group}
                    adminUserId={adminUserId}
                    onViewDetail={onViewDetail}
                    actionLoading={actionLoading}
                    onAction={handleAction}
                    onPayAll={handlePayAll}
                    payAllLoading={payAllLoading[group.referrer_user_id]}
                    forceExpanded={allExpanded}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Load More */}
        {!loading && !error && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: colors.gray200, borderTop: `1px solid ${colors.border}` }}>
            <span style={{ fontSize: '13px', color: colors.gray600 }}>
              Showing {loadedCount} of {totalCount} referral{totalCount !== 1 ? 's' : ''}
              {groups.length > 0 && ` across ${groups.length} referrer${groups.length !== 1 ? 's' : ''}`}
            </span>
            {hasMore && (
              <button
                type="button"
                disabled={loadingMore}
                onClick={handleLoadMore}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '0 20px', height: '36px', lineHeight: '1',
                  background: loadingMore ? colors.gray300 : gradients.primary,
                  color: loadingMore ? colors.gray500 : '#fff',
                  border: 'none', borderRadius: '4px', fontWeight: 600, fontSize: '13px',
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                  WebkitAppearance: 'none', appearance: 'none', boxShadow: 'none', textShadow: 'none',
                }}
              >
                {loadingMore ? 'Loading...' : `Load More (${totalCount - loadedCount} remaining)`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralList;
